/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Thursday, April 7th 2022, 11:40:40 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { isEmpty } from "lodash";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import { APISERVICEPATHS } from "lib/api";
import { FormField, FormFieldAPIQueryParams, FormConfiguration } from "lib/schemas";
import { isValidUUID } from "lib/utils";
import { AsyncAPIListAutoComplete, HelperText } from "ui-widgets";
import { useFormBuilderContext } from "form-builder/providers";
import { useFormFieldsAPI } from "form-builder/services";
import { InputIcon } from "theme/icons";
import { COLORSCHEME } from "theme/styles";

export interface FieldPickerProps {
  label: string;
  fieldIdentifier: FormField["id"] | FormField["artifact_guid"];
  handleChooseField: (field: FormField) => void;
  onLoadIntial?: (field: FormField) => void;
  formId: FormConfiguration["id"];
  helpText?: string;
  apiQueryParams?: FormFieldAPIQueryParams;
}

export const FieldPicker = ({
  label,
  fieldIdentifier,
  handleChooseField,
  onLoadIntial,
  formId,
  helpText,
  apiQueryParams = {},
  ...props
}: FieldPickerProps) => {
  // // This valid check ensures a value is never undefined unless it is a hyphen so it can be resolved in AsyncAPIListAutoComplete
  // // TODO: have the API return a null value instead of this hyphen.
  const validFieldTag = fieldIdentifier !== "-" && isValidUUID(fieldIdentifier?.toString()) && fieldIdentifier;

  const { useFetch } = useFormFieldsAPI();
  const { apiItem, isMakingAPIRequest, fetchItem } = useFetch();

  const fetchedField = validFieldTag ? apiItem?.[0] ?? {} : apiItem;

  React.useEffect(() => {
    if (validFieldTag) {
      fetchItem(null, { apiQueryParams: { ...apiQueryParams, artifact_guid: validFieldTag } });
      return;
    }

    if (fieldIdentifier && Number.isInteger(parseInt(fieldIdentifier.toString()))) {
      fetchItem(fieldIdentifier, { apiQueryParams });
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldIdentifier, validFieldTag]);

  React.useEffect(() => {
    !isEmpty(fetchedField) && onLoadIntial?.(fetchedField);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(fetchedField)]);

  const { useNavigateToFormBuilder } = useFormBuilderContext();
  const { navigateToFormBuilder } = useNavigateToFormBuilder();

  return (
    <>
      <AsyncAPIListAutoComplete<FormField>
        {...props}
        id="field-select-dropdown"
        label={label}
        itemIdProperty="id"
        getOptionLabel={(option) => option.label}
        loadingText="Loading Fields"
        onChangeCallBack={handleChooseField}
        value={fetchedField}
        apiServicePath={APISERVICEPATHS.formFields}
        apiQueryParams={formId ? { ...apiQueryParams, form: formId } : apiQueryParams}
        // disableClearable
        inputStartAdornment={
          isMakingAPIRequest ? <CircularProgress /> : <InputIcon width={20} height={20} fill={COLORSCHEME.primary} />
        }
        fullWidth
        noOptionsText={
          formId ? (
            <Button variant="text" onClick={() => navigateToFormBuilder(formId)}>
              No fields for selected form. Add fields to this form?
            </Button>
          ) : null
        }
      />
      {helpText && <HelperText>{helpText}</HelperText>}
    </>
  );
};
